/* ============================================================================
   harvest-2026.js  ::  The 2026 Harvest landing page (task 0309, board card 84)

   Scope note: this file drives ONE thing, the live season arc. The mosaic is
   rendered server side in Liquid (it was JS generated in the preview) because
   this page is indexable and early indexing is the stated reason for its launch
   date. The anticipation vs shoppable state is also set server side, as a class
   on the section root, so there is no flash of the wrong state on load.

   Everything here is defensive: if the arc markup is missing the script exits
   quietly rather than throwing, because a console error on an indexable page is
   a real cost.
   ========================================================================= */
(function () {
  'use strict';

  var MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  /* The arc is a quadratic bezier drawn in the SVG. These control points MUST
     stay in sync with the `d` attribute in harvest-2026.liquid. If you move the
     path, move these. */
  var P0 = { x: 40,  y: 210 };
  var P1 = { x: 500, y: -34 };
  var P2 = { x: 960, y: 210 };

  function pointAt(u) {
    var m = 1 - u;
    return {
      x: m * m * P0.x + 2 * m * u * P1.x + u * u * P2.x,
      y: m * m * P0.y + 2 * m * u * P1.y + u * u * P2.y
    };
  }

  /* x is linear along this curve because P1.x is the midpoint of P0.x and P2.x,
     so the arc's height at any x can be read directly. */
  function arcYAtX(x) {
    var u = (x - P0.x) / (P2.x - P0.x);
    return pointAt(Math.max(0, Math.min(1, u))).y;
  }

  /* Place the "N% through the season" label at the clear spot nearest the sun.
     A fixed anchor rule cannot do this: on the falling side of the arc an
     end-anchored label runs left, into the part of the arc that is higher, and
     sits on the stroke (card 518). So every candidate box above the sun is
     tested against the arc and the sun halo, and the closest one that clears
     both wins. All units are viewBox units, so the result holds at any width. */
  var VB_W = 1000;
  var LBL_ASC = 12, LBL_DESC = 4, LBL_PAD = 10, SUN_R = 26, EDGE = 8;

  function boxClear(l, r, t, b, pt) {
    /* sun halo: nearest point of the box to the sun centre */
    var nx = Math.max(l, Math.min(pt.x, r));
    var ny = Math.max(t, Math.min(pt.y, b));
    var dx = nx - pt.x, dy = ny - pt.y;
    if (dx * dx + dy * dy < (SUN_R + LBL_PAD) * (SUN_R + LBL_PAD)) { return false; }
    /* arc: sample it across the box's padded x span */
    for (var x = Math.max(P0.x, l - LBL_PAD); x <= Math.min(P2.x, r + LBL_PAD); x += 2) {
      var y = arcYAtX(x);
      if (y >= t - LBL_PAD && y <= b + LBL_PAD) { return false; }
    }
    return true;
  }

  function placeLabel(txt, pt, chars) {
    var w = 0;
    try { w = txt.getComputedTextLength(); } catch (e) { w = 0; }
    if (!w) { w = chars * 14 * 0.6; } /* monospace fallback when not laid out */

    var best = null;
    for (var by = pt.y - SUN_R; by - LBL_ASC >= 0; by -= 2) {
      for (var l = pt.x - w - SUN_R; l <= pt.x + SUN_R; l += 4) {
        if (l < EDGE || l + w > VB_W - EDGE) { continue; }
        if (!boxClear(l, l + w, by - LBL_ASC, by + LBL_DESC, pt)) { continue; }
        var cx = l + w / 2 - pt.x;
        var cy = by - LBL_ASC / 2 - pt.y;
        var d = cx * cx + cy * cy;
        if (!best || d < best.d) { best = { l: l, by: by, d: d }; }
      }
    }

    if (best) {
      txt.setAttribute('text-anchor', 'start');
      txt.setAttribute('x', best.l.toFixed(1));
      txt.setAttribute('y', best.by.toFixed(1));
    } else {
      txt.setAttribute('text-anchor', 'middle');
      txt.setAttribute('x', Math.max(EDGE + w / 2, Math.min(VB_W - EDGE - w / 2, pt.x)).toFixed(1));
      txt.setAttribute('y', LBL_ASC.toFixed(1));
    }
  }

  /* Parse YYYY-MM-DD as a LOCAL date. `new Date('2026-04-15')` parses as UTC and
     lands on the previous day for anyone west of Greenwich, which is everyone
     reading this page from the Rogue Valley. */
  function parseLocalDate(s, fallback) {
    if (!s) { return fallback; }
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s).trim());
    if (!m) { return fallback; }
    var d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return isNaN(d.getTime()) ? fallback : d;
  }

  function initArc(root) {
    var svg = root.querySelector('[data-hv-arc]');
    if (!svg) { return; }

    var start = parseLocalDate(root.getAttribute('data-hv-season-start'), new Date(2026, 3, 15));
    var end   = parseLocalDate(root.getAttribute('data-hv-season-end'),   new Date(2026, 9, 20));
    if (end <= start) { return; }

    var now = new Date();
    var t = (now - start) / (end - start);
    t = Math.max(0, Math.min(1, t));

    var pt = pointAt(t);

    var sun = svg.querySelector('[data-hv-sun]');
    if (sun) {
      sun.setAttribute('transform', 'translate(' + pt.x.toFixed(1) + ',' + pt.y.toFixed(1) + ')');
    }

    /* the travelled portion of the arc, drawn by revealing a dashed stroke */
    var done = svg.querySelector('[data-hv-arcdone]');
    if (done && typeof done.getTotalLength === 'function') {
      var L = done.getTotalLength();
      done.style.strokeDasharray = L;
      done.style.strokeDashoffset = L * (1 - t);
    }

    var stamp = root.querySelector('[data-hv-stamp]');
    if (stamp) {
      stamp.textContent = 'As of ' + MON[now.getMonth()] + ' ' + now.getDate() + ', ' + now.getFullYear();
    }

    var lbl = svg.querySelector('[data-hv-sunlabel]');
    if (lbl) {
      while (lbl.firstChild) { lbl.removeChild(lbl.firstChild); }
      var txt = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      txt.textContent = Math.round(t * 100) + '% through the season';
      lbl.appendChild(txt);
      placeLabel(txt, pt, txt.textContent.length);
      /* Courier Prime may land after first paint; re-measure once it has */
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(function () { placeLabel(txt, pt, txt.textContent.length); });
      }
    }

    var arcnow = root.querySelector('[data-hv-arcnow]');
    if (arcnow) {
      /* phase label is resolved server side so the strip and the arc cannot
         disagree about which phase we are in */
      var phase = root.getAttribute('data-hv-current-phase');
      arcnow.textContent = phase
        ? MON[now.getMonth()].toUpperCase() + ' · ' + phase.toUpperCase()
        : MON[now.getMonth()].toUpperCase();
    }
  }

  function init() {
    var roots = document.querySelectorAll('[data-hv-root]');
    for (var i = 0; i < roots.length; i++) {
      initArc(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Shopify theme editor: re-run when this section is re-rendered, otherwise the
     sun sits at its unpositioned default while Nathan is editing. */
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector ? e.target.querySelector('[data-hv-root]') : null;
    if (root) { initArc(root); }
  });
})();

/* ============================================================================
   BUY SECTION (card 558). One server-rendered flower-feature-buybox per
   available product, all but one [hidden]. The chips only swap which unit
   shows. Nothing here computes price, pool grams or availability: those are
   server-rendered by the reused snippets and decorated by pdp-flower.js.

   The shared snippets emit ONE section id and byte-identical option radio ids
   for every unit, which makes a variant change in one unit re-render every other
   unit's form, and makes a label click drive the FIRST unit's radio. The
   isolation below is ported from assets/flower-feature.js (task 0362), where each
   step is documented at length and was verified live; the comments here only
   name what each step is for. pdp-flower.js, flower-feature.js and the snippets
   are NOT edited, and flower-feature.js is not loaded on this page.
   ========================================================================= */
(function () {
  'use strict';

  /* section ids of buy units on this page, for the qty-status patch */
  var unitSectionIds = Object.create(null);
  var qtyPatched = false;

  /* product-form._updateQtyStatus re-fetches with the unit's isolated section id,
     which the product template cannot render, and would throw after every add.
     Skip it for buy units only; refreshUnitStock below repaints the stock line. */
  function patchQtyStatusRefetch() {
    if (qtyPatched) { return; }
    var Ctor = window.customElements && window.customElements.get('product-form');
    if (!Ctor || !Ctor.prototype || !Ctor.prototype._updateQtyStatus) { return; }
    qtyPatched = true;
    var original = Ctor.prototype._updateQtyStatus;
    Ctor.prototype._updateQtyStatus = function () {
      var sid = this.sectionId || this.getAttribute('section-id') || '';
      if (unitSectionIds[sid]) { return; }
      return original.apply(this, arguments);
    };
  }

  function cssEsc(str) { return (str || '').replace(/["\\]/g, '\\$&'); }

  function setElementSectionId(el, uid) {
    el.setAttribute('section-id', uid);
    if ('sectionId' in el) { el.sectionId = uid; }
  }

  /* unique radio ids, labels re-pointed, and each unit's radios owned by its own
     form so the units stop sharing one document-wide radio group */
  function rescopeOptionDom(unit, uid, formId) {
    var selector = unit.querySelector('product-variant-selector');
    if (!selector) { return; }
    var suffix = '--u-' + uid;
    var radios = selector.querySelectorAll('input[type="radio"]');
    Array.prototype.forEach.call(radios, function (input) {
      if (formId && input.getAttribute('form') !== formId) { input.setAttribute('form', formId); }
      var oldId = input.id;
      var newId;
      if (input.hasAttribute('data-hv-scoped')) {
        newId = oldId;
      } else if (oldId) {
        newId = oldId + suffix;
        input.id = newId;
        input.setAttribute('data-hv-scoped', '');
      } else {
        return;
      }
      if (oldId && oldId !== newId) {
        var lbls = selector.querySelectorAll('label[for="' + cssEsc(oldId) + '"]');
        Array.prototype.forEach.call(lbls, function (l) { l.setAttribute('for', newId); });
      }
    });
  }

  function isolateUnit(unit, sectionId) {
    var handle = unit.getAttribute('data-cultivar');
    if (!handle) { return; }
    var uid = sectionId + '--' + handle;
    unitSectionIds[uid] = true;
    var root = unit.querySelector('[data-pdpf-root]');
    if (root) { root.setAttribute('data-section-id', uid); }
    ['product-variant-selector[section-id]', 'stock-alert[section-id]', 'product-form[section-id]'].forEach(function (sel) {
      var el = unit.querySelector(sel);
      if (el) { setElementSectionId(el, uid); }
    });
    var formId = (root && root.getAttribute('data-form-id')) || '';
    rescopeOptionDom(unit, uid, formId);
    var picker = unit.querySelector('select[id^="product-variant-picker-"]');
    if (picker) {
      var oldId = picker.id;
      var newId = 'product-variant-picker-' + uid;
      picker.id = newId;
      var lbl = unit.querySelector('label[for="' + cssEsc(oldId) + '"]');
      if (lbl) { lbl.setAttribute('for', newId); }
    }
  }

  function readJSON(el) {
    if (!el) { return []; }
    try { return JSON.parse(el.textContent) || []; } catch (e) { return []; }
  }

  /* the shared selector renders no radio checked for these products; check the
     radios of its current variant so the pills show the selection */
  function preselect(unit) {
    var selector = unit.querySelector('product-variant-selector');
    if (!selector) { return; }
    var optionsData = readJSON(selector.querySelector('[data-options-with-values]'));
    var variants = readJSON(selector.querySelector('[data-variants-json]'));
    if (!optionsData.length || !variants.length) { return; }
    var anyChecked = optionsData.some(function (opt) {
      return !!selector.querySelector('input[name="' + cssEsc((opt && opt.name) || '') + '"]:checked');
    });
    if (anyChecked) { return; }
    var wantId = parseInt(selector.getAttribute('current-variant-id'), 10);
    var target = null;
    if (wantId) { target = variants.filter(function (v) { return v.id === wantId; })[0] || null; }
    if (!target) { target = variants[0]; }
    if (!target || !target.options) { return; }
    optionsData.forEach(function (opt, i) {
      var name = (opt && opt.name) || '';
      var val = target.options[i];
      if (!name || val == null) { return; }
      var input = selector.querySelector('input[name="' + cssEsc(name) + '"][value="' + cssEsc(val) + '"]');
      if (input && !input.checked) { input.checked = true; }
    });
  }

  /* pdp-flower.js only runs its full decorate() on boot and on a variantChange
     for the section id it captured at boot, which may be the shared id or the
     isolated one depending on module timing. Fire one display-only variantChange
     per candidate id; html is a clone of the unit's own form so product-form's
     listener re-reads unchanged button states. */
  function forceDecorate(unit, sectionId) {
    var form = unit.querySelector('product-form');
    if (!form) { return; }
    var holder = document.createElement('div');
    holder.appendChild(form.cloneNode(true));
    if (!holder.querySelector('[data-add-button]') || !holder.querySelector('[data-sold-out-button]')) { return; }
    var uid = sectionId + '--' + (unit.getAttribute('data-cultivar') || '');
    [uid, sectionId].forEach(function (sid) {
      document.dispatchEvent(new CustomEvent('variantChange', {
        bubbles: true,
        detail: { sectionId: sid, html: holder, variant: null, fromHv: true }
      }));
    });
  }

  /* after an add, repaint THIS unit's stock line from the product's own page */
  function refreshUnitStock(unit) {
    var stockAlert = unit.querySelector('stock-alert');
    if (!stockAlert || typeof stockAlert._update !== 'function') { return; }
    var host = unit.querySelector('product-form[product-url]') || unit.querySelector('product-variant-selector[product-url]');
    var productUrl = host && host.getAttribute('product-url');
    if (!productUrl) { return; }
    var input = unit.querySelector('product-form [data-variant-id-input]');
    var vid = input && input.value;
    var url = productUrl + (productUrl.indexOf('?') === -1 ? '?' : '&') + (vid ? 'variant=' + vid : '');
    fetch(url, { headers: { 'Accept': 'text/html' } })
      .then(function (r) { return r.text(); })
      .then(function (text) {
        var fresh = new DOMParser().parseFromString(text, 'text/html').querySelector('stock-alert');
        if (!fresh) { return; }
        var wrapper = document.createElement('div');
        wrapper.appendChild(fresh);
        stockAlert._update(wrapper);
      })
      .catch(function () {});
  }

  function initBuy(section) {
    if (section.__hvBuyBooted) { return; }
    section.__hvBuyBooted = true;
    var sectionId = section.getAttribute('data-hv-section-id') || '';
    var units = Array.prototype.slice.call(section.querySelectorAll('[data-hv-unit]'));
    var chips = Array.prototype.slice.call(section.querySelectorAll('[data-hv-chip]'));
    if (!units.length) { return; }

    units.forEach(function (u) { isolateUnit(u, sectionId); });
    patchQtyStatusRefetch();
    if (window.customElements && window.customElements.whenDefined) {
      window.customElements.whenDefined('product-form').then(patchQtyStatusRefetch);
    }

    var shown = [];
    function ensureDecorated(unit) {
      preselect(unit);
      if (shown.indexOf(unit) !== -1) { return; }
      shown.push(unit);
      /* setTimeout, not rAF: rAF pauses in background tabs */
      setTimeout(function () { forceDecorate(unit, sectionId); }, 0);
    }
    window.addEventListener('load', function () {
      shown.forEach(function (u) { forceDecorate(u, sectionId); });
    });

    function select(handle) {
      var unit = null;
      units.forEach(function (u) {
        var on = u.getAttribute('data-cultivar') === handle;
        if (on) { u.removeAttribute('hidden'); unit = u; } else { u.setAttribute('hidden', ''); }
      });
      chips.forEach(function (c) {
        c.setAttribute('aria-pressed', String(c.getAttribute('data-hv-chip') === handle));
      });
      if (unit) { ensureDecorated(unit); }
    }

    chips.forEach(function (c) {
      c.addEventListener('click', function () { select(c.getAttribute('data-hv-chip')); });
    });

    units.forEach(function (unit) {
      var root = unit.querySelector('[data-pdpf-root]');
      var form = root && document.getElementById(root.getAttribute('data-form-id') || '');
      if (!form) { return; }
      form.addEventListener('submit', function () {
        var attempts = 0;
        var last = -1;
        (function poll() {
          attempts += 1;
          fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              var total = cart.total_price || 0;
              if ((total === last && attempts > 1) || attempts >= 8) { refreshUnitStock(unit); return; }
              last = total;
              setTimeout(poll, 300);
            })
            .catch(function () { if (attempts < 8) { setTimeout(poll, 300); } });
        })();
      });
    });

    var first = units.filter(function (u) { return !u.hasAttribute('hidden'); })[0] || units[0];
    select(first.getAttribute('data-cultivar'));
  }

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-hv-buy]'), initBuy);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var s = e.target.querySelector ? e.target.querySelector('[data-hv-buy]') : null;
    if (s) { initBuy(s); }
  });
})();
